import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { LoadingCard } from "../components/LoadingSpinner";
import PhoneInput from "../components/ui/PhoneInput";
import { formatPhone } from "../utils/phoneUtils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Wrench,
  DollarSign,
  Eye,
  Edit,
  Trash2,
} from "lucide-react";
import { subcontractorService } from "../services/subcontractorService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";

export default function Subcontractors() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(null);
  const [editSubcontractor, setEditSubcontractor] = useState(null);
  const [newSubcontractor, setNewSubcontractor] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    specialty: "",
    rate_per_hour: "",
    notes: ""
  });
  const [showCustomSpecialty, setShowCustomSpecialty] = useState(false);
  const [showEditCustomSpecialty, setShowEditCustomSpecialty] = useState(false);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  // API hooks
  const { data: subcontractorsData, isLoading: loading } = useQuery({
    queryKey: ['subcontractors', searchTerm],
    queryFn: () => subcontractorService.getAll({ search: searchTerm || undefined })
  });
  
  const createSubcontractorMutation = useMutation({
    mutationFn: (data) => subcontractorService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcontractors'] });
      toast({ description: "Subcontractor created successfully!" });
      setNewSubcontractor({
        name: "",
        email: "",
        phone: "",
        address: "",
        specialty: "",
        rate_per_hour: "",
        notes: ""
      });
      setShowCustomSpecialty(false);
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to create subcontractor"
      });
    }
  });

  const updateSubcontractorMutation = useMutation({
    mutationFn: ({ id, data }) => subcontractorService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subcontractors'] });
      toast({ description: "Subcontractor updated successfully!" });
      setIsEditDialogOpen(false);
      setEditSubcontractor(null);
      setShowEditCustomSpecialty(false);
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to update subcontractor"
      });
    }
  });

  const subcontractors = subcontractorsData?.data || [];

  const handleAddSubcontractor = async () => {
    const subcontractorData = {
      ...newSubcontractor,
      rate_per_hour: newSubcontractor.rate_per_hour ? parseFloat(newSubcontractor.rate_per_hour) : undefined,
      email: newSubcontractor.email || undefined,
      phone: newSubcontractor.phone || undefined,
      address: newSubcontractor.address || undefined,
      specialty: newSubcontractor.specialty || undefined,
      notes: newSubcontractor.notes || undefined
    };
    
    createSubcontractorMutation.mutate(subcontractorData);
  };

  const handleViewSubcontractor = (subcontractor) => {
    setSelectedSubcontractor(subcontractor);
    setIsViewDialogOpen(true);
  };

  const handleEditSubcontractor = (subcontractor) => {
    const standardSpecialties = ["irrigation", "hardscaping", "tree_services", "lighting", "fencing", "drainage", "Pools", "Concrete", "excavation"];
    const isCustomSpecialty = subcontractor.specialty && !standardSpecialties.includes(subcontractor.specialty);
    
    setEditSubcontractor({
      ...subcontractor,
      rate_per_hour: subcontractor.rate_per_hour?.toString() || ""
    });
    setShowEditCustomSpecialty(isCustomSpecialty);
    setIsEditDialogOpen(true);
  };

  const handleUpdateSubcontractor = async () => {
    if (!editSubcontractor) return;
    
    const subcontractorId = editSubcontractor.id || editSubcontractor._id;
    const updateData = {
      ...editSubcontractor,
      rate_per_hour: parseFloat(editSubcontractor.rate_per_hour) || null
    };
    
    // Remove MongoDB _id from update data
    delete updateData._id;
    
    updateSubcontractorMutation.mutate({
      id: subcontractorId,
      data: updateData
    });
  };

  const [deleteClicks, setDeleteClicks] = React.useState({});

  const handleDeleteSubcontractor = async (subcontractor) => {
    const subcontractorId = subcontractor.id || subcontractor._id;
    const currentClicks = deleteClicks[subcontractorId] || 0;
    
    if (currentClicks === 0) {
      console.warn(`⚠️ DELETE WARNING: Click delete again to confirm deletion of "${subcontractor.name}"`);
      setDeleteClicks(prev => ({ ...prev, [subcontractorId]: 1 }));
      setTimeout(() => {
        setDeleteClicks(prev => ({ ...prev, [subcontractorId]: 0 }));
      }, 3000);
      return;
    }
    
    try {
      console.log(`🗑️ Deleting subcontractor: ${subcontractor.name}`);
      await subcontractorService.delete(subcontractorId);
      window.location.reload();
    } catch (error) {
      console.error("❌ Failed to delete subcontractor:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Subcontractors</h1>
          <p className="text-gray-600 mt-1">Manage your network of subcontractors and specialists</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand-600 hover:bg-brand-700 w-full sm:w-auto shrink-0">
              <Plus className="mr-2 h-4 w-4" />
              Add Subcontractor
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Subcontractor</DialogTitle>
              <DialogDescription>
                Add a new subcontractor to your network.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={newSubcontractor.name}
                  onChange={(e) => setNewSubcontractor({...newSubcontractor, name: e.target.value})}
                  placeholder="John's Irrigation Services"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newSubcontractor.email}
                  onChange={(e) => setNewSubcontractor({...newSubcontractor, email: e.target.value})}
                  placeholder="john@irrigation.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <PhoneInput
                  id="phone"
                  value={newSubcontractor.phone}
                  onChange={(val) => setNewSubcontractor({...newSubcontractor, phone: val})}
                  placeholder="916.232.2332"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="specialty">Specialty</Label>
                <Select 
                  value={showCustomSpecialty ? "other" : newSubcontractor.specialty}
                  onValueChange={(value) => {
                    if (value === "other") {
                      setShowCustomSpecialty(true);
                      setNewSubcontractor({...newSubcontractor, specialty: ""});
                    } else {
                      setShowCustomSpecialty(false);
                      setNewSubcontractor({...newSubcontractor, specialty: value});
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="irrigation">Irrigation</SelectItem>
                    <SelectItem value="hardscaping">Hardscaping</SelectItem>
                    <SelectItem value="tree_services">Tree Services</SelectItem>
                    <SelectItem value="lighting">Lighting</SelectItem>
                    <SelectItem value="fencing">Fencing</SelectItem>
                    <SelectItem value="drainage">Drainage</SelectItem>
                    <SelectItem value="Pools">Pools</SelectItem>
                    <SelectItem value="Concrete">Concrete</SelectItem>
                    <SelectItem value="excavation">Excavation</SelectItem>
                    <SelectItem value="other">Other (Custom)</SelectItem>
                  </SelectContent>
                </Select>
                {showCustomSpecialty && (
                  <Input
                    id="custom-specialty"
                    value={newSubcontractor.specialty}
                    onChange={(e) => setNewSubcontractor({...newSubcontractor, specialty: e.target.value})}
                    placeholder="Enter custom specialty..."
                    className="mt-2"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="rate">Hourly Rate</Label>
                <Input
                  id="rate"
                  type="number"
                  value={newSubcontractor.rate_per_hour}
                  onChange={(e) => setNewSubcontractor({...newSubcontractor, rate_per_hour: e.target.value})}
                  placeholder="75"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={newSubcontractor.address}
                  onChange={(e) => setNewSubcontractor({...newSubcontractor, address: e.target.value})}
                  placeholder="123 Business St, City, State"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newSubcontractor.notes}
                  onChange={(e) => setNewSubcontractor({...newSubcontractor, notes: e.target.value})}
                  placeholder="Additional notes about this subcontractor..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddSubcontractor}
                disabled={createSubcontractorMutation.isPending || !newSubcontractor.name}
                className="bg-brand-600 hover:bg-brand-700"
              >
                {createSubcontractorMutation.isPending ? "Adding..." : "Add Subcontractor"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* View Subcontractor Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Subcontractor Details</DialogTitle>
            <DialogDescription>
              View information about {selectedSubcontractor?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedSubcontractor && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Name</Label>
                  <p className="text-sm text-gray-600">{selectedSubcontractor.name}</p>
                </div>
                <div>
                  <Label className="font-semibold">Specialty</Label>
                  <p className="text-sm text-gray-600">
                    {selectedSubcontractor.specialty ? 
                      selectedSubcontractor.specialty.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                      : 'Not specified'
                    }
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Email</Label>
                  <p className="text-sm text-gray-600">{selectedSubcontractor.email || 'Not provided'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Phone</Label>
                  <p className="text-sm text-gray-600">{formatPhone(selectedSubcontractor.phone) || 'Not provided'}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Hourly Rate</Label>
                  <p className="text-sm text-gray-600">
                    {selectedSubcontractor.rate_per_hour ? `$${selectedSubcontractor.rate_per_hour}/hr` : 'Not set'}
                  </p>
                </div>
                <div>
                  <Label className="font-semibold">Status</Label>
                  <Badge className={selectedSubcontractor.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}>
                    {selectedSubcontractor.status || 'active'}
                  </Badge>
                </div>
              </div>
              <div>
                <Label className="font-semibold">Address</Label>
                <p className="text-sm text-gray-600">{selectedSubcontractor.address || 'Not provided'}</p>
              </div>
              {selectedSubcontractor.notes && (
                <div>
                  <Label className="font-semibold">Notes</Label>
                  <p className="text-sm text-gray-600">{selectedSubcontractor.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                setIsViewDialogOpen(false);
                handleEditSubcontractor(selectedSubcontractor);
              }}
              className="bg-brand-600 hover:bg-brand-700"
            >
              Edit Subcontractor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subcontractor Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Subcontractor</DialogTitle>
            <DialogDescription>
              Update information for {editSubcontractor?.name}
            </DialogDescription>
          </DialogHeader>
          {editSubcontractor && (
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={editSubcontractor.name}
                  onChange={(e) => setEditSubcontractor({...editSubcontractor, name: e.target.value})}
                  placeholder="John's Irrigation Services"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editSubcontractor.email}
                  onChange={(e) => setEditSubcontractor({...editSubcontractor, email: e.target.value})}
                  placeholder="john@irrigation.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <PhoneInput
                  id="edit-phone"
                  value={editSubcontractor.phone}
                  onChange={(val) => setEditSubcontractor({...editSubcontractor, phone: val})}
                  placeholder="916.232.2332"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-specialty">Specialty</Label>
                <Select 
                  value={showEditCustomSpecialty ? "other" : (editSubcontractor.specialty || "")} 
                  onValueChange={(value) => {
                    if (value === "other") {
                      setShowEditCustomSpecialty(true);
                      setEditSubcontractor({...editSubcontractor, specialty: ""});
                    } else {
                      setShowEditCustomSpecialty(false);
                      setEditSubcontractor({...editSubcontractor, specialty: value});
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="irrigation">Irrigation</SelectItem>
                    <SelectItem value="hardscaping">Hardscaping</SelectItem>
                    <SelectItem value="tree_services">Tree Services</SelectItem>
                    <SelectItem value="lighting">Lighting</SelectItem>
                    <SelectItem value="fencing">Fencing</SelectItem>
                    <SelectItem value="drainage">Drainage</SelectItem>
                    <SelectItem value="Pools">Pools</SelectItem>
                    <SelectItem value="Concrete">Concrete</SelectItem>
                    <SelectItem value="excavation">Excavation</SelectItem>
                    <SelectItem value="other">Other (Custom)</SelectItem>
                  </SelectContent>
                </Select>
                {showEditCustomSpecialty && (
                  <Input
                    id="edit-custom-specialty"
                    value={editSubcontractor.specialty || ""}
                    onChange={(e) => setEditSubcontractor({...editSubcontractor, specialty: e.target.value})}
                    placeholder="Enter custom specialty..."
                    className="mt-2"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-rate">Hourly Rate</Label>
                <Input
                  id="edit-rate"
                  type="number"
                  value={editSubcontractor.rate_per_hour}
                  onChange={(e) => setEditSubcontractor({...editSubcontractor, rate_per_hour: e.target.value})}
                  placeholder="75"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={editSubcontractor.address}
                  onChange={(e) => setEditSubcontractor({...editSubcontractor, address: e.target.value})}
                  placeholder="123 Business St, City, State"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editSubcontractor.notes}
                  onChange={(e) => setEditSubcontractor({...editSubcontractor, notes: e.target.value})}
                  placeholder="Additional notes about this subcontractor..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateSubcontractor}
              disabled={updateSubcontractorMutation.isPending || !editSubcontractor?.name}
              className="bg-brand-600 hover:bg-brand-700"
            >
              {updateSubcontractorMutation.isPending ? "Updating..." : "Update Subcontractor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search subcontractors by name, specialty, or contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Subcontractors Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Subcontractors ({subcontractors.length})</CardTitle>
          <CardDescription>
            Manage your network of specialized contractors
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingCard>Loading subcontractors...</LoadingCard>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="md:hidden space-y-3">
                {subcontractors.map((subcontractor) => (
                  <div key={subcontractor._id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium flex items-center">
                          <Wrench className="mr-2 h-4 w-4 text-brand-600 shrink-0" />
                          {subcontractor.name}
                        </div>
                        <p className="text-sm text-gray-600 ml-6">
                          {subcontractor.specialty ?
                            subcontractor.specialty.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
                            : 'General Services'
                          }
                        </p>
                      </div>
                      <div className="flex shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => handleViewSubcontractor(subcontractor)} className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEditSubcontractor(subcontractor)} className="h-8 w-8 p-0">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteSubcontractor(subcontractor)} className={`h-8 w-8 p-0 ${(deleteClicks[subcontractor.id || subcontractor._id] || 0) > 0 ? "text-white bg-red-600 hover:bg-red-700" : "text-red-600 hover:text-red-700"}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 space-y-1 ml-6">
                      {subcontractor.email && (
                        <div className="text-sm flex items-center text-gray-600">
                          <Mail className="mr-1.5 h-3 w-3 shrink-0" />
                          <span className="truncate">{subcontractor.email}</span>
                        </div>
                      )}
                      {subcontractor.phone && (
                        <div className="text-sm flex items-center text-gray-600">
                          <Phone className="mr-1.5 h-3 w-3 shrink-0" />
                          <span>{formatPhone(subcontractor.phone)}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap ml-6">
                      {subcontractor.rate_per_hour ? (
                        <span className="text-sm text-green-600 font-medium flex items-center">
                          <DollarSign className="mr-0.5 h-3 w-3" />
                          {subcontractor.rate_per_hour}/hr
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">Rate not set</span>
                      )}
                      <Badge className={subcontractor.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}>
                        {subcontractor.status || 'active'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table view */}
              <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name & Specialty</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subcontractors.map((subcontractor) => (
                  <TableRow key={subcontractor._id}>
                    <TableCell>
                      <div>
                        <div className="font-medium flex items-center">
                          <Wrench className="mr-2 h-4 w-4 text-brand-600" />
                          {subcontractor.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {subcontractor.specialty ? 
                            subcontractor.specialty.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) 
                            : 'General Services'
                          }
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {subcontractor.email && (
                          <div className="text-sm flex items-center">
                            <Mail className="mr-1 h-3 w-3" />
                            {subcontractor.email}
                          </div>
                        )}
                        {subcontractor.phone && (
                          <div className="text-sm flex items-center">
                            <Phone className="mr-1 h-3 w-3" />
                            {formatPhone(subcontractor.phone)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {subcontractor.rate_per_hour ? (
                        <div className="flex items-center text-green-600 font-medium">
                          <DollarSign className="mr-1 h-4 w-4" />
                          {subcontractor.rate_per_hour}/hr
                        </div>
                      ) : (
                        <span className="text-gray-400">Not set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={subcontractor.status === 'active' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}>
                        {subcontractor.status || 'active'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleViewSubcontractor(subcontractor)}
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEditSubcontractor(subcontractor)}
                          title="Edit subcontractor"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDeleteSubcontractor(subcontractor)}
                          className={`${
                            (deleteClicks[subcontractor.id || subcontractor._id] || 0) > 0 
                              ? "text-white bg-red-600 hover:bg-red-700" 
                              : "text-red-600 hover:text-red-700 hover:bg-red-50"
                          }`}
                          title={
                            (deleteClicks[subcontractor.id || subcontractor._id] || 0) > 0 
                              ? "Click again to confirm deletion" 
                              : "Delete subcontractor"
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}